// ArchiveProcedure.tsx
import { FC, useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import ArchiveChecklistStackedMonthChart from "./ArchiveChecklistStackedMonthChart";
import { ArchiveProcedureFilterBar } from "./ArchiveProcedureFilterBar";
import { useArchiveProcedureFilters } from "./useArchiveProcedureFilters";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import {
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import { ArchiveProcedureTourSteps } from "@/features/archive/ui/ArchiveProcedureTourSteps";
import HistoryContainer from "@/features/archive/ui/components/history/HistoryContainer";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import { formatUtcDateTime } from "@/shared/lib";
import Spinner from "@/shared/ui/Spinner";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Stack,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTour } from "@reactour/tour";
import dayjs from "dayjs";

import {
	useFetchArchivedProcedure,
	useFetchArchivedProcedures,
} from "@/service/ArchiveQueries";

const PAGE_PADDING = { xs: 2, sm: 2.5 };

const panelSx = (t: Theme) => ({
	borderRadius: { xs: 2, sm: 3 },
	paddingBottom: 2,
	border: getSurfaceBorderStyle(t, "strong"),
	backgroundColor: t.palette.background.paper,
	boxShadow:
		t.palette.mode === "dark"
			? `0 1px 0 ${alpha(t.palette.common.white, 0.04)}`
			: `0 1px 2px ${alpha(t.palette.common.black, 0.04)}`,
	overflow: "hidden",
});

const scrollSx = (t: Theme) => ({
	"&::-webkit-scrollbar": { width: 6 },
	"&::-webkit-scrollbar-track": { background: "transparent" },
	"&::-webkit-scrollbar-thumb": {
		borderRadius: 2,
		background: alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.18 : 0.16),
	},
	"&::-webkit-scrollbar-thumb:hover": {
		background: alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.28 : 0.24),
	},
});

const headerTitleSx = {
	fontWeight: 700,
	letterSpacing: "-0.02em",
	lineHeight: 1.1,
};

const sectionTitleSx = {
	fontWeight: 700,
	letterSpacing: "-0.02em",
	lineHeight: 1.2,
};

const compactChipSx = (bgColor: string, color: string) => ({
	height: 26,
	borderRadius: 2,
	fontWeight: 700,
	bgcolor: bgColor,
	color,
	border: `1px solid ${alpha(color, 0.14)}`,
});

export const ArchiveProcedure: FC = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const { timezone } = useTime();
	const { id } = useParams<{ id: string }>();
	const { setSteps, setCurrentStep } = useTour();
	const [searchParams] = useSearchParams();
	const splitPanelHeight = isMobile ? undefined : 560;

	// ✅ One source of truth for chart + history
	const {
		filters,
		setStartDate,
		setEndDate,
		setSelectedUser,
		setDateRange,
		options,
	} = useArchiveProcedureFilters();

	useEffect(() => {
		setCurrentStep(0);
		if (setSteps) setSteps(ArchiveProcedureTourSteps);
	}, [setSteps, setCurrentStep]);

	const { data: procedure, isLoading: procedureLoading } =
		useFetchArchivedProcedure();
	const { data: archivedList, isLoading: archivedLoading } =
		useFetchArchivedProcedures(options);

	const completionsCount = archivedList?.ArchivedProjects?.length ?? 0;
	const uniqueCompleters = useMemo(() => {
		const ids = new Set<number>();
		for (const project of archivedList?.ArchivedProjects ?? []) {
			for (const user of project.Users ?? []) {
				ids.add(user.UserId);
			}
		}
		return ids.size;
	}, [archivedList?.ArchivedProjects]);

	const firstCompleted = useMemo(() => {
		const list = archivedList?.ArchivedProjects ?? [];
		let first: string | null = null;
		for (const p of list) {
			for (const update of p?.Updates ?? []) {
				if (!update.Date) continue;
				if (
					first === null ||
					dayjs.utc(update.Date).valueOf() < dayjs.utc(first).valueOf()
				) {
					first = String(update.Date);
				}
			}
		}
		return first;
	}, [archivedList?.ArchivedProjects]);

	const lastCompleted = useMemo(() => {
		const list = archivedList?.ArchivedProjects ?? [];
		let last: string | null = null;
		for (const p of list) {
			for (const update of p?.Updates ?? []) {
				if (!update.Date) continue;
				if (
					last === null ||
					dayjs.utc(update.Date).valueOf() > dayjs.utc(last).valueOf()
				) {
					last = String(update.Date);
				}
			}
		}
		return last;
	}, [archivedList?.ArchivedProjects]);

	// Invalid id (no procedure after load)
	if (!procedureLoading && !procedure && id) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					backgroundColor: "background.default",
				}}
			>
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						px: PAGE_PADDING,
					}}
				>
					<Typography color="text.secondary">
						<Translate String="Archive.Procedure.NotFound">
							Procedure not found
						</Translate>
					</Typography>
				</Box>
			</Box>
		);
	}

	const rangeText = `${dayjs(filters.startDate).format("DD/MM/YY")} → ${dayjs(filters.endDate).format("DD/MM/YY")}`;
	const backToArchiveUrl = useMemo(() => {
		const search = searchParams.toString();
		return search ? `/archive?${search}` : "/archive";
	}, [searchParams]);
	const rangeDays = Math.max(
		1,
		dayjs(filters.endDate).diff(dayjs(filters.startDate), "day") + 1,
	);
	const completionsPerDay = Number((completionsCount / rangeDays).toFixed(2));
	const summaryItems = [
			{
				label: "Created",
				value: procedure?.Created
					? formatUtcDateTime(procedure.Created, timezone)
					: "—",
			},
			{
				label: "Updated",
				value: procedure?.Updated
					? formatUtcDateTime(procedure.Updated, timezone)
					: "—",
			},
			{
				label: "First completed",
				value: firstCompleted
					? formatUtcDateTime(firstCompleted, timezone).split(" ")[0]
					: "—",
			},
			{
				label: "Last completed",
				value: lastCompleted
					? formatUtcDateTime(lastCompleted, timezone)
					: "—",
			},
	];

	return (
		<Box
			data-tour="archive-procedure"
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minHeight: 0,
				overflow: "hidden",
				backgroundColor: "background.default",
			}}
		>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					width: "100%",
					overflowY: "auto",
					overflowX: "hidden",
					p: PAGE_PADDING,
					...scrollSx(theme),
					WebkitOverflowScrolling: "touch",
				}}
			>
				<Stack
					spacing={2}
					sx={{
						mx: "auto",
						width: "100%",
						minWidth: 0,
					}}
				>
					{/* Standard page header: title, compact metadata, primary action. */}
					<Card
						variant="outlined"
						data-tour="archive-procedure-header"
						sx={{
							...panelSx(theme),
							borderColor: alpha(theme.palette.primary.main, 0.14),
						}}
					>
						<CardContent
							sx={{
								px: { xs: 2, sm: 2.5 },
								py: { xs: 1.5, sm: 2 },
								"&:last-child": { pb: { xs: 1.5, sm: 2 } },
							}}
						>
							{procedureLoading && !procedure ? (
								<Box
									sx={{ display: "grid", placeItems: "center", minHeight: 120 }}
								>
									<Spinner
										text={
											<Translate String="Archive.Procedure.Loading">
												Loading procedure…
											</Translate>
										}
									/>
								</Box>
							) : (
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: {
											xs: "1fr",
											sm: "minmax(0, 1fr) auto",
										},
										gap: 1.5,
										alignItems: "start",
									}}
								>
									<Box sx={{ minWidth: 0 }}>
										<Typography
											component={Link}
											to={backToArchiveUrl}
											variant="caption"
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.5,
												color: "text.secondary",
												fontWeight: 700,
												textDecoration: "none",
												letterSpacing: "0.04em",
												textTransform: "uppercase",
												mb: 0.75,
												"&:hover": { color: "primary.main" },
											}}
										>
											<ArchiveOutlinedIcon sx={{ fontSize: 13 }} />
											← Back to archive
										</Typography>

										<Typography
											variant="h5"
											component="h1"
											sx={{
												...headerTitleSx,
												fontSize: { xs: "1.15rem", sm: "1.4rem" },
											}}
										>
											{procedure?.Name ?? "—"}
										</Typography>

										{procedure?.Description ? (
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{
													mt: 0.5,
													lineHeight: 1.6,
													display: "-webkit-box",
													WebkitLineClamp: 1,
													WebkitBoxOrient: "vertical",
													overflow: "hidden",
													maxWidth: 860,
												}}
											>
												{procedure.Description}
											</Typography>
										) : null}

										<Stack
											direction="row"
											flexWrap="wrap"
											gap={0.75}
											alignItems="center"
											useFlexGap
											sx={{ mt: 1 }}
										>
											<Chip
												size="small"
												label={rangeText}
												sx={compactChipSx(
													alpha(theme.palette.primary.main, 0.1),
													theme.palette.primary.main,
												)}
											/>
											<Chip
												size="small"
												label={
													archivedLoading
														? "Loading…"
														: `${completionsCount} completion${completionsCount === 1 ? "" : "s"}`
												}
												sx={compactChipSx(
													alpha(theme.palette.success.main, 0.12),
													theme.palette.mode === "dark"
														? theme.palette.success.light
														: theme.palette.success.dark,
												)}
											/>
											<Chip
												size="small"
												label="Archived"
												sx={compactChipSx(
													alpha(theme.palette.text.primary, 0.06),
													theme.palette.text.secondary,
												)}
											/>
										</Stack>

										<Stack
											direction="row"
											flexWrap="wrap"
											gap={{ xs: 1.5, sm: 2.5 }}
											useFlexGap
											sx={{
												mt: 1.25,
												pt: 1.25,
												borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
											}}
										>
											{summaryItems.map((item) => (
												<Box key={item.label} sx={{ minWidth: 0 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{
															display: "block",
															fontWeight: 700,
															textTransform: "uppercase",
															letterSpacing: "0.06em",
														}}
													>
														{item.label}
													</Typography>
													<Typography
														variant="body2"
														sx={{ mt: 0.2, fontWeight: 600, lineHeight: 1.4 }}
													>
														{item.value}
													</Typography>
												</Box>
											))}
										</Stack>
									</Box>

									{id && (
										<Link
											to={`/archive/procedure/${id}/data`}
											style={{
												textDecoration: "none",
												width: isMobile ? "100%" : undefined,
											}}
										>
											<ProcedureButton
												intent="primary"
												size="medium"
												fullWidth={isMobile}
												startIcon={<TableChartIcon />}
												sx={{
													minHeight: 40,
													px: 2,
													boxShadow: "none",
												}}
											>
												<Translate String="Archive.Procedure.ViewData">
													View full data
												</Translate>
											</ProcedureButton>
										</Link>
									)}
								</Box>
							)}
						</CardContent>
					</Card>


					<ArchiveProcedureFilterBar
						startDate={filters.startDate}
						endDate={filters.endDate}
						selectedUser={filters.selectedUser}
						setStartDate={setStartDate}
						setEndDate={setEndDate}
						setSelectedUser={setSelectedUser}
						setDateRange={setDateRange}
					/>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								lg: "minmax(0, 1fr) minmax(0, 1fr)",
							},
							gap: 2,
							alignItems: "stretch",
						}}
					>
						<Card
							variant="outlined"
							data-tour="archive-procedure-chart"
							sx={{
								...panelSx(theme),
								height: splitPanelHeight ?? "auto",
								display: "flex",
								flexDirection: "column",
								minWidth: 0,
							}}
						>
							<Box
								sx={{
									px: { xs: 2, sm: 2.5 },
									pt: { xs: 2, sm: 2.25 },
									pb: { xs: 1.25, sm: 1.5 },
									borderBottom: getSurfaceBorderStyle(theme, "subtle"),
								}}
							>
								<Typography variant="subtitle1" sx={sectionTitleSx}>
									<Translate String="Archive.Procedure.ChartTitle">
										Checklist time breakdown
									</Translate>
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mt: 0.5, lineHeight: 1.6 }}
								>
									<Translate String="Auto.Features.Archive.Pages.ArchiveProcedure.ArchiveProcedure.L627C10.ReviewChecklistDurationByMonthThen">Review checklist duration by month, then drill into individual
									completions when needed.</Translate>
								</Typography>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.75, fontWeight: 700 }}
								>
									{rangeText}
								</Typography>
								<Stack
									direction="row"
									spacing={0.75}
									flexWrap="wrap"
									useFlexGap
									sx={{ mt: 1 }}
								>
									<Chip
										size="small"
										label={`${completionsCount} completions`}
										sx={compactChipSx(
											alpha(theme.palette.primary.main, 0.1),
											theme.palette.primary.main,
										)}
									/>
									<Chip
										size="small"
										label={`${uniqueCompleters} completers`}
										sx={compactChipSx(
											alpha(theme.palette.success.main, 0.12),
											theme.palette.mode === "dark"
												? theme.palette.success.light
												: theme.palette.success.dark,
										)}
									/>
								</Stack>
							</Box>

							<CardContent
								sx={{
									p: 0,
									flex: 1,
									minHeight: 0,
									overflowY: splitPanelHeight ? "auto" : "visible",
								}}
							>
								<ArchiveChecklistStackedMonthChart
									startDate={filters.startDate}
									endDate={filters.endDate}
									selectedUser={filters.selectedUser}
									archivedProcedures={archivedList}
									isLoading={archivedLoading}
									onMonthClick={({ start, end }) => {
										// Drilldown updates the SAME filters used everywhere
										setDateRange(start, end);
									}}
								/>
							</CardContent>
						</Card>

						<HistoryContainer
							title={
								<Translate String="Archive.History.Header">History</Translate>
							}
							subtitle={
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: 500 }}
								>
									{rangeText}
								</Typography>
							}
							initialRowsPerPage={10}
							embeddedInProcedurePage
							options={options}
							archivedProcedures={archivedList}
							isLoadingArchived={archivedLoading}
							containerHeight={splitPanelHeight}
						/>
					</Box>
				</Stack>
			</Box>
		</Box>
	);
};
